import { AHSeparator, AHText, Box, useLayoutContext } from "arkh";

import {
  AHImageHorizontalCollection,
  AHImageHorizontalCollectionItem,
} from "../../../components";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import React, { useCallback } from "react";
import { RouteProp } from "@react-navigation/native";
import { ScrollView } from "react-native";
import { TaskExecutionInstructionsParamList } from "./TaskExecutionInstructionsStack";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { graphql, useLazyLoadQuery } from "react-relay";
import { TaskExecutionInstructionsScreenQuery } from "./__generated__/TaskExecutionInstructionsScreenQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { useNewFetchKeyOnNav } from "../../../useNewFetchKeyOnNav";

import { useLightboxControls } from "@/src/common/lightbox/LightboxProvider";

export const TaskExecutionInstructionsScreen = (props: {
  route: RouteProp<
    TaskExecutionInstructionsParamList,
    "TaskExecutionInstructions"
  >;
  navigation: NativeStackNavigationProp<
    TaskExecutionInstructionsParamList,
    "TaskExecutionInstructions"
  >;
}) => {
  const navFetch = useNewFetchKeyOnNav();
  return (
    <LazyQueryNetworkBoundrary>
      {useCallback(
        (fetchKey) => (
          <InstructionComponent
            navigation={props.navigation}
            taskId={props.route.params.taskId}
            fetchKey={navFetch + fetchKey}
          />
        ),
        [navFetch, props.navigation, props.route.params.taskId]
      )}
    </LazyQueryNetworkBoundrary>
  );
};

function InstructionComponent(props: {
  taskId: string;
  fetchKey: string;
  navigation: NativeStackNavigationProp<
    TaskExecutionInstructionsParamList,
    "TaskExecutionInstructions"
  >;
}) {
  const layout = useLayoutContext();
  const insets = useSafeAreaInsets();

  const data = useLazyLoadQuery<TaskExecutionInstructionsScreenQuery>(
    graphql`
      query TaskExecutionInstructionsScreenQuery($taskId: ID!) {
        task(id: $taskId) {
          id
          title
          instructions
          images {
            id
            sizes {
              height
              width
            }
            ...Image_image
          }
        }
      }
    `,
    {
      taskId: props.taskId,
    }
  );

  const lightbox = useLightboxControls();
  if (!data.task) {
    return null;
  }
  const task = data.task;

  return (
    <ScrollView
      contentContainerStyle={{
        paddingBottom: insets.bottom + layout.space.large,
      }}
      scrollIndicatorInsets={{
        bottom: insets.bottom,
      }}
    >
      <Box color="surface">
        {task.images.length > 0 ? (
          <>
            <Box
              style={{
                alignSelf: "stretch",
              }}
            >
              <AHImageHorizontalCollection imagesToShow={2}>
                {task.images.map((image, i) => {
                  return (
                    <AHImageHorizontalCollectionItem
                      key={image.id}
                      type="remote"
                      image={image}
                      onPress={() => {
                        lightbox.openLightbox({
                          images: task.images.map((image) => ({
                            image: {
                              type: "remote",
                              image: image,
                            },
                            mediaType: "image" as const,
                            dimensions: {
                              width: image.sizes?.[0]?.width ?? 1,
                              height: image.sizes?.[0]?.height ?? 1,
                            },
                          })),
                          index: i,
                        });
                      }}
                    />
                  );
                })}
              </AHImageHorizontalCollection>
            </Box>
            <AHSeparator />
          </>
        ) : null}
        <AHText
          spaceHorizontal="medium"
          spaceVertical="large"
          type="title"
          selectable
        >
          {task.title}
        </AHText>
        {task.instructions && task.instructions.length > 0 ? (
          <AHText
            spaceTop="small"
            spaceBottom="xxlarge"
            spaceHorizontal="medium"
            style={{
              flex: 1,
            }}
            selectable
            type="body"
          >
            {task.instructions}
          </AHText>
        ) : null}
        <AHSeparator />
      </Box>
    </ScrollView>
  );
}
